import React, { useRef, useState } from 'react'
import {
    Card,
    CardContent,
    Typography,
    Box,
    IconButton,
    styled,
    Backdrop,
    MenuItem,
} from '@mui/material'
import { Icon } from 'packages/eid-icons'
import { isNilOrEmpty } from 'packages/core'
import { defaultApplicationLogoImage, getFullImageUrl } from 'utils'
import { Dropdown } from 'packages/eid-ui'
import { useTranslation } from 'react-i18next'

interface SSOApplicationsCardProps {
    applicationName: string
    iconUrl: string
    link: string
    handleRemoveAppFromCategory?: () => void
}

const StyledBackdrop = styled(Backdrop)(() => ({
    zIndex: '1201',
}))

const StyledMenuItem = styled(MenuItem)({
    height: '3rem',
})

const SSOApplicationsCard: React.FC<SSOApplicationsCardProps> = ({
    applicationName,
    iconUrl,
    link,
    handleRemoveAppFromCategory,
}) => {
    const [isMoreOptionsOpen, setIsMoreOptionsOpen] = useState(false)
    const anchorRef = useRef(null)
    const { t } = useTranslation()

    let moreOptions = [
        {
            label: 'LaunchApplication',
            onClick: () => window.open(link, '_blank'),
            display: Boolean(link),
        },
        {
            label: 'RemoveFromCategory',
            onClick: handleRemoveAppFromCategory,
            display: Boolean(handleRemoveAppFromCategory),
        },
    ]

    moreOptions = moreOptions.filter((option) => option.display)

    return (
        <Card
            sx={{
                width: '17.2rem',
                height: '21rem',
                borderRadius: '0.8rem !important',
                boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.05);',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '4.2rem 1.6rem',
                position: 'relative',
                transition: 'transform 0.2s, box-shadow 0.2s',
                cursor: link ? 'pointer' : 'normal',
                '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 6,
                },
            }}
            data-ai-explain={`SSO application: ${applicationName}`}
            data-ai-explain-section="listing"
            onClick={() =>
                link && !isMoreOptionsOpen
                    ? window.open(link, '_blank')
                    : undefined
            }
        >
            {moreOptions.length > 0 && (
                <IconButton
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                    size="small"
                    aria-label="More options"
                    aria-expanded={isMoreOptionsOpen}
                    aria-haspopup="menu"
                    data-ai-explain={`More actions for ${applicationName}`}
                    onClick={(event) => {
                        event.stopPropagation()
                        setIsMoreOptionsOpen(true)
                    }}
                >
                    <Icon name="EllipsisDots" color="#5D6870" />
                    <span
                        ref={anchorRef}
                        style={{
                            position: 'absolute',
                            top: 35,
                            left: 28,
                        }}
                    />
                </IconButton>
            )}

            {isMoreOptionsOpen && (
                <>
                    <StyledBackdrop
                        sx={{ color: '#fff' }}
                        open={isMoreOptionsOpen}
                        onClick={(event) => {
                            event.stopPropagation()
                            setIsMoreOptionsOpen(false)
                        }}
                    />
                    <Dropdown
                        anchorEl={anchorRef.current}
                        showCone={true}
                        keepMounted
                        open={isMoreOptionsOpen}
                        onClose={() => setIsMoreOptionsOpen(false)}
                        listStyles={{
                            paddingBottom: '0',
                            '& > ul': {
                                paddingBottom: '0.7rem',
                                paddingTop: '0',
                            },
                        }}
                        iconPosition={{
                            top: '-0.5rem',
                            right: '0',
                        }}
                        width="auto"
                        disableScrollLock={false}
                    >
                        {moreOptions.map((option) => (
                            <StyledMenuItem
                                tabIndex={0}
                                onClick={(event) => {
                                    event.stopPropagation()
                                    option?.onClick?.()
                                    setIsMoreOptionsOpen(false)
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault()
                                        event.stopPropagation()
                                        option?.onClick?.()
                                        setIsMoreOptionsOpen(false)
                                    }
                                }}
                                data-ai-explain={`${t(option.label)}: ${applicationName}`}
                            >
                                {t(option.label)}
                            </StyledMenuItem>
                        ))}
                    </Dropdown>
                </>
            )}

            <Box>
                <img
                    style={{ width: '9rem', height: '9rem' }}
                    src={
                        isNilOrEmpty(iconUrl)
                            ? getFullImageUrl(defaultApplicationLogoImage)
                            : getFullImageUrl(iconUrl)
                    }
                    alt={`${applicationName} application logo`}
                />
            </Box>

            <CardContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: 0,
                }}
            >
                <Typography
                    variant="body1"
                    sx={{
                        fontSize: 14,
                        fontWeight: 'bold',
                        textAlign: 'center',
                        color: '#333',
                        wordBreak: 'break-word',
                    }}
                >
                    {applicationName}
                </Typography>
            </CardContent>
        </Card>
    )
}

export default SSOApplicationsCard
